import { Injectable } from '@angular/core';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { NotificationMessage, NotificationObject } from './notification.types';
import { CommunicationActivityNotificationHandlerService } from './communication-activity-notification-handler.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectOrganizationNotificationHandlerService {
  constructor(
    private readonly translatePipe: TranslatePipe,
    private readonly communicationHandler: CommunicationActivityNotificationHandlerService
  ) {}

  // Project notification handlers
  public handleProjectNotifications(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    const projectNotificationCode: string = notificationCode.replace('_YOUR', '');
    message.label = this.translatePipe.transform(projectNotificationCode, {
      company_name: object.companyName ?? '',
      notifiedBy: object.notifiedBy ?? '',
      project_name: object.projectName ?? '',
    });
  }

  public handleProjectUserNotifications(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(notificationCode, {
      company_name: object.companyName ?? '',
      notifiedTarget: object.notifiedTarget ?? '',
      notifiedBy: object.notifiedBy ?? '',
      project_name: object.projectName ?? '',
    });
  }

  public handleProjectUserRolesUpdated(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    const roles: string = object.notifiedTargetRolesCodes
      ? object.notifiedTargetRolesCodes.map((role: string) => this.translatePipe.translateRole(role)).join(', ')
      : '';
    message.label = this.translatePipe.transform(notificationCode, {
      notifiedTargetRoles: roles,
      notifiedTarget: object.notifiedTarget ?? '',
      notifiedBy: object.notifiedBy ?? '',
      project_name: object.projectName ?? '',
    });
  }

  // Sub-Section notification handlers
  public handleSubsectionCreated(originalCode: string, object: NotificationObject, message: NotificationMessage): void {
    message.label = this.translatePipe.transform(originalCode, {
      notifiedBy: object.notifiedBy ?? '',
      sectionName: object.sectionName ?? '',
      subSectionName: this.communicationHandler.findSubsectionName(object.subSectionName),
      project_name: object.projectName ?? '',
    });
  }

  public handleSubsectionDeleted(originalCode: string, object: NotificationObject, message: NotificationMessage): void {
    message.label = this.translatePipe.transform(originalCode, {
      notifiedBy: object.notifiedBy ?? '',
      projectName: object.projectName ?? '', // This translation uses PROJECTNAME without underscore
      sectionName: object.sectionName ?? '',
      subSectionName: this.communicationHandler.findSubsectionName(object.subSectionName),
    });
  }

  // Sub-Section Item notification handlers
  public handleSubsectionItemCreated(
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(originalCode, {
      notifiedBy: object.notifiedBy ?? '',
      subSectionName: this.communicationHandler.findSubsectionName(object.subSectionName),
      item: object.item ?? '',
    });
  }

  public handleSubsectionItemApproved(
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(originalCode, {
      notifiedBy: object.notifiedBy ?? '',
      subSectionName: this.communicationHandler.findSubsectionName(object.subSectionName),
      itemName: object.itemName ?? '',
      editingType: object.editingType ?? '',
    });
  }

  public handleProjectStatusUpdated(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    const baseCode: string = notificationCode.replace('_SELF', '').replace('_YOUR', '');
    message.label = this.translatePipe.transform(baseCode, {
      notifiedBy: object.notifiedBy ?? '',
      project_name: object.projectName ?? '',
      old_status: object.projectOldStatus ? this.translatePipe.transform(`task_${object.projectOldStatus}`) : '',
      new_status: object.projectNewStatus ? this.translatePipe.transform(`task_${object.projectNewStatus}`) : '',
    });
  }

  public handleCampaignStatusUpdated(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    const baseCode: string = notificationCode.replace('_SELF', '').replace('_YOUR', '');
    message.label = this.translatePipe.transform(baseCode, {
      notifiedBy: object.notifiedBy ?? '',
      campaign_name: object.campaignName ?? '',
      old_status: object.campaignOldStatus ? this.translatePipe.transform(`task_${object.campaignOldStatus}`) : '',
      new_status: object.campaignNewStatus ? this.translatePipe.transform(`task_${object.campaignNewStatus}`) : '',
    });
  }

  public handleAssetListStatusUpdated(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    const baseCode: string = notificationCode.replace('_SELF', '').replace('_YOUR', '');
    message.label = this.translatePipe.transform(baseCode, {
      notifiedBy: object.notifiedBy ?? '',
      assetlist_name: object.assetListName ?? '',
      old_status: object.assetListOldStatus ? this.translatePipe.transform(`task_${object.assetListOldStatus}`) : '',
      new_status: object.assetListNewStatus ? this.translatePipe.transform(`task_${object.assetListNewStatus}`) : '',
    });
  }

  public handleProjectDate(originalCode: string, object: NotificationObject, message: NotificationMessage): void {
    message.label = this.translatePipe.transform(originalCode, {
      date_type: object.changedDate ?? '',
      notifiedBy: object.notifiedBy ?? '',
      old_date: object.oldDate ?? '',
      new_date: object.newDate ?? '',
    });
  }

  // Workspace notification handlers
  public handleWorkspaceNotifications(
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    invitedRoleName?: string
  ): void {
    message.label = this.translatePipe.transform(originalCode, {
      notifiedBy: object.notifiedBy ?? '',
      workspaceName: object.workspaceName ?? '',
      roleName: invitedRoleName ?? '',
      oldWorkspace: object.oldWorkspaceName ?? '',
    });
  }

  public handleWorkspaceMovedProject(
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(originalCode, {
      notifiedBy: object.notifiedBy ?? '',
      projectName: object.projectName ?? '', // This translation uses PROJECTNAME without underscore
      newWorkspaceName: object.workspaceName ?? '',
      oldWorkspace: object.oldWorkspaceName ?? '',
    });
  }

  public handleWorkspaceEditedUser(
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    oldRole?: string,
    newRole?: string
  ): void {
    message.label = this.translatePipe.transform(originalCode, {
      notifiedBy: object.notifiedBy ?? '',
      companyName: object.companyName ?? '',
      oldRole: oldRole ?? '',
      newRole: newRole ?? '',
    });
  }

  // Company notification handlers
  public handleChangedCompanyRole(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    oldRole?: string,
    newRole?: string
  ): void {
    message.label = this.translatePipe.transform(notificationCode, {
      companyName: object.notifiedByCompanyName ?? '',
      username: object.notifiedByUsername ?? '',
      oldRole: oldRole ?? '',
      newRole: newRole ?? '',
    });
    message.action = {
      showCompany: true,
      companyName: object.notifiedByCompanyName ?? '',
    };
  }

  public handleInvitedToCompany(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(notificationCode, {
      roleName: object.roleName ?? '',
      inviterName: object.notifiedByUsername ?? '',
    });
    message.action = {
      showCompany: true,
      companyName: object.notifiedByCompanyName ?? '',
    };
  }

  public handleCompanyOwnershipTransferred(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(notificationCode, {
      oldUsername: object.oldOwner ?? '',
      newUsername: object.newOwner ?? '',
    });
    message.action = {
      showCompany: true,
      companyName: object.notifiedByCompanyName ?? '',
    };
  }

  public handleCompanyUserRemoved(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(notificationCode, {
      username: object.notifiedBy ?? '',
    });
    message.action = {
      showCompany: true,
      companyName: object.notifiedByCompanyName ?? '',
    };
  }
}
